import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Edit, Percent, Calendar, Tag, Building, Globe, CheckCircle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import useTaxRateStore from '@/stores/taxRateStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'

const TaxRateDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    currentTaxRate,
    loading,
    error,
    fetchTaxRateById,
    clearError,
    clearCurrentTaxRate
  } = useTaxRateStore()

  useEffect(() => {
    if (id) {
      fetchTaxRateById(id)
    }

    return () => {
      clearCurrentTaxRate()
    }
  }, [id])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (enabled: boolean) => {
    return enabled ? (
      <Badge variant="default" className="flex items-center gap-1">
        <CheckCircle className="h-3 w-3" />
        Actif
      </Badge>
    ) : (
      <Badge variant="destructive" className="flex items-center gap-1">
        <XCircle className="h-3 w-3" />
        Inactif
      </Badge>
    )
  }

  const getTypeBadge = (type: string) => {
    const typeMap: Record<string, string> = {
      'vat': 'VAT (TVA)',
      'gst': 'GST',
      'pst': 'PST',
      'hst': 'HST',
      'sales_tax': 'Taxe de vente',
      'other': 'Autre'
    }
    const colorMap: Record<string, string> = {
      'vat': 'bg-blue-100 text-blue-800',
      'gst': 'bg-green-100 text-green-800',
      'pst': 'bg-purple-100 text-purple-800',
      'hst': 'bg-orange-100 text-orange-800',
      'sales_tax': 'bg-red-100 text-red-800',
      'other': 'bg-gray-100 text-gray-800'
    }
    
    return (
      <Badge variant="outline" className={colorMap[type]}>
        {typeMap[type] || type}
      </Badge>
    )
  }

  if (loading && !currentTaxRate) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!currentTaxRate && !loading) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Taux de taxe non trouvé"
          subtitle="Le taux de taxe que vous recherchez n'existe pas ou a été supprimé."
          breadcrumbs={[
            { label: 'Taux de taxe', href: '/tax-rates' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/tax-rates"
        />
        <Alert>
          <AlertDescription>
            Ce taux de taxe n'a pas pu être chargé. Il a peut-être été supprimé ou vous n'avez pas les permissions nécessaires.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!currentTaxRate) {
    return null
  }

  const breadcrumbs = [
    { label: 'Taux de taxe', href: '/tax-rates' },
    { label: currentTaxRate.name, href: '#' }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: () => navigate(`/tax-rates/${id}/edit`),
      variant: 'default' as const
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title={currentTaxRate.name}
        subtitle={`Informations détaillées sur le taux de taxe ${currentTaxRate.name}`}
        breadcrumbs={breadcrumbs}
        backUrl="/tax-rates"
        actions={actions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-4" onClick={clearError}>
              Fermer
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Informations générales" subtitle="Détails de base et configuration du taux de taxe">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-full">
                <Percent className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{currentTaxRate.name}</h2>
                <p className="text-muted-foreground">
                  Taux: {currentTaxRate.rate}% • Type: {getTypeBadge(currentTaxRate.type || 'other')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Statut</h3>
                  <div className="mt-1">
                    {getStatusBadge(currentTaxRate.enabled)}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Type de taxe</h3>
                  <p className="mt-1 text-lg font-medium">
                    {getTypeBadge(currentTaxRate.type)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Classification du type de taxe
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Pays associé</h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <Globe className="h-5 w-5 text-muted-foreground" />
                    <span className="text-lg font-medium">
                      ID: {currentTaxRate.country_id}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Identifiant du pays auquel cette taxe appartient
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Taux de taxe</h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <Tag className="h-5 w-5 text-muted-foreground" />
                    <span className="text-lg font-medium">
                      {currentTaxRate.rate}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Taux de taxe applicable
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Région spécifique</h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <Building className="h-5 w-5 text-muted-foreground" />
                    <span className="text-lg font-medium">
                      {currentTaxRate.region_id || 'Toutes les régions'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {currentTaxRate.region_id 
                      ? 'Ce taux de taxe s\'applique uniquement à cette région' 
                      : 'Ce taux de taxe s\'applique à toutes les régions du pays'}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Exemple de calcul</h3>
                  <div className="mt-2 p-3 bg-muted rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Montant HT:</span>
                      <span className="font-medium">100.00 €</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm">Taxe ({currentTaxRate.rate}%):</span>
                      <span className="font-medium">{(100 * (currentTaxRate.rate || 0) / 100).toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t">
                      <span className="text-sm font-medium">Montant TTC:</span>
                      <span className="font-bold">
                        {(100 + (100 * (currentTaxRate.rate || 0) / 100)).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>
        </div>

        {/* Métadonnées et actions */}
        <div className="space-y-6">
          <DetailSection title="Métadonnées" subtitle="Informations techniques et dates">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Identifiant</h3>
                <p className="mt-1 font-mono text-sm bg-muted p-2 rounded">
                  {currentTaxRate.id}
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Date d'effet</h3>
                    <p className="mt-1">
                      {formatDate(currentTaxRate.effective_from)}
                    </p>
                  </div>
                </div>

                {currentTaxRate.effective_to && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Date de fin</h3>
                      <p className="mt-1">
                        {formatDate(currentTaxRate.effective_to)}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Créé le</h3>
                    <p className="mt-1">
                      {formatDate(currentTaxRate.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Dernière modification</h3>
                    <p className="mt-1">
                      {formatDate(currentTaxRate.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Actions rapides">
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate(`/tax-rates/${id}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Modifier ce taux de taxe
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/tax-rates')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à la liste
              </Button>
            </div>
          </DetailSection>

          <DetailSection title="Statut actuel">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Validité</span>
                {currentTaxRate.effective_to && new Date(currentTaxRate.effective_to) < new Date() ? (
                  <Badge variant="destructive">Expiré</Badge>
                ) : (
                  <Badge variant="default">Valide</Badge>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Application</span>
                <Badge variant="outline">
                  {currentTaxRate.region_id ? 'Régionale' : 'Nationale'}
                </Badge>
              </div>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default TaxRateDetails
